import * as THREE from 'three';
import { Transform } from './Transform';
import { UUID, Vector3, Quaternion } from '../types';
import { SceneNodeAnimator } from './SceneNodeAnimator';


export abstract class SceneNode {
    // Unique identifier
    public readonly id: UUID;
    
    // Transform component
    public readonly transform: Transform;
    
    // Node properties
    public name: string;
    public visible: boolean = true;
    public active: boolean = true;
    
    // The visual representation of this node in the THREE.js scene
    protected _mesh: THREE.Object3D | null = null;
    
    // Bounding box for culling and picking
    protected _boundingBox: THREE.Box3 = new THREE.Box3();
    protected _isBoundingBoxDirty: boolean = true;
    
    // Parent-child relationships (mirrors transform hierarchy)
    private _parent: SceneNode | null = null;
    private _children: SceneNode[] = [];
    private _animator: SceneNodeAnimator | null = null;

    constructor(name: string = 'SceneNode', transform?: Transform) {
        this.id = this.generateUUID();
        this.name = name;
        this.transform = transform || Transform.createInheritable();
    }

    // Parent-child relationship management
    setParent(parent: SceneNode | null): void {
        if (this._parent === parent) return;

        // Remove from old parent
        if (this._parent) {
            const index = this._parent._children.indexOf(this);
            if (index !== -1) {
                this._parent._children.splice(index, 1);
            }
        }

        // Set new parent
        this._parent = parent;
        if (parent) {
            parent._children.push(this);
            // Sync transform hierarchy
            this.transform.setParent(parent.transform);
            // Sync Three.js scene graph hierarchy
            if (parent._mesh && this._mesh) {
                parent._mesh.add(this._mesh);
            }
        } else {
            this.transform.setParent(null);
        }

        this.onParentChanged(parent);
    }

    getParent(): SceneNode | null {
        return this._parent;
    }

    getChildren(): SceneNode[] {
        return [...this._children]; // Return copy to prevent external modification
    }

    addChild(child: SceneNode): void {
        child.setParent(this);
    }

    removeChild(child: SceneNode): void {
        child.setParent(null);
    }

    // Hierarchy traversal
    getRoot(): SceneNode {
        let root: SceneNode = this;
        while (root._parent) {
            root = root._parent;
        }
        return root;
    }

    getDepth(): number {
        let depth = 0;
        let current: SceneNode | null = this._parent;
        while (current) {
            depth++;
            current = current._parent;
        }
        return depth;
    }

    // Find nodes in hierarchy
    findChild(name: string): SceneNode | null {
        for (const child of this._children) {
            if (child.name === name) {
                return child;
            }
        }
        return null;
    }

    findChildById(id: UUID): SceneNode | null {
        for (const child of this._children) {
            if (child.id === id) {
                return child;
            }
        }
        return null;
    }

    findInChildren(predicate: (node: SceneNode) => boolean): SceneNode | null {
        for (const child of this._children) {
            if (predicate(child)) {
                return child;
            }
            const found = child.findInChildren(predicate);
            if (found) {
                return found;
            }
        }
        return null;
    }

    // Traverse all descendants
    traverseChildren(callback: (node: SceneNode) => void): void {
        for (const child of this._children) {
            callback(child);
            child.traverseChildren(callback);
        }
    }

    // Get all descendants as flat array
    getAllChildren(): SceneNode[] {
        const result: SceneNode[] = [];
        this.traverseChildren(node => result.push(node));
        return result;
    }

    /**
     * Recursively collects all THREE.Object3D instances from this node and its children.
     * This is used to add all renderable parts of a scene node to the main THREE.Scene.
     */
    /**
     * Ensures this node's Three.js object is correctly parented.
     * Call after async mesh creation when setParent() was called before _mesh existed.
     */
    public syncThreeJsParent(): void {
        if (!this._mesh || !this._parent?._mesh) return;
        if (this._mesh.parent !== this._parent._mesh) {
            this._parent._mesh.add(this._mesh);
            // Force transform sync on next frame since mesh was just re-parented
            this.transform.markDirty();
        }
    }

    public getThreeObjects(): THREE.Object3D[] {
        // With the new architecture, we only need to return the top-level mesh.
        // Three.js will handle rendering the children automatically.
        if (this._mesh) {
            return [this._mesh];
        }
        return [];
    }

    // Lifecycle methods (to be overridden by subclasses)
    protected onParentChanged(newParent: SceneNode | null): void {
        const oldParent = this.getParent();
        // When the parent changes, we need to update the THREE.js scene graph.
        // Remove mesh from old parent if it exists.
        if (this._mesh && oldParent?._mesh) {
            oldParent._mesh.remove(this._mesh);
        }

        // Add mesh to new parent if it exists.
        if (newParent?._mesh && this._mesh) {
            newParent._mesh.add(this._mesh);
        }
    }

    protected onChildAdded(child: SceneNode): void {
        // When a child is added, sync its mesh to our mesh.
        if (this._mesh && child._mesh) {
            this._mesh.add(child._mesh);
        }
    }

    protected onChildRemoved(child: SceneNode): void {
        // When a child is removed, sync its mesh.
        if (this._mesh && child._mesh) {
            this._mesh.remove(child._mesh);
        }
    }

    // Update methods (called by scene manager)
    update(deltaTime: number): void {
        if (!this.active) return;

        // Sync custom Transform → Three.js Object3D (unified for all node types)
        this.syncTransformToThreeJS();

        // The transform might have been updated by an animation, making the box dirty.
        if (this.transform.isDirty) {
            this.markBoundingBoxDirty();
        }

        this.onUpdate(deltaTime);

        // Update all children
        for (const child of this._children) {
            child.update(deltaTime);
        }
    }

    /**
     * Unified transform sync: propagates this node's Transform properties
     * to the underlying Three.js Object3D (_mesh / Group).
     *
     * - If this node's Three.js object has a Three.js parent (i.e. it is a
     *   child in the Three.js scene graph), we write LOCAL position/rotation/scale
     *   and let Three.js compute the world matrix automatically.
     * - If it is a root object in the Three.js scene, we write the WORLD matrix
     *   ourselves and disable matrixAutoUpdate.
     *
     * This replaces the scattered sync code that was duplicated in POI.onUpdate(),
     * Mesh3D.onUpdate(), LabelMesh, etc.
     */
    protected syncTransformToThreeJS(): void {
        if (!this._mesh) return;

        // Most nodes mirror the SceneNode hierarchy in the Three.js graph and
        // therefore receive LOCAL values. Detached render objects (notably
        // CSS3D labels, which live in a separate CSS scene) still have a
        // logical SceneNode parent but no matching Three.js parent. Those must
        // receive WORLD values or the parent transform is lost visually.
        const hasMirroredThreeParent = !!this._parent?._mesh && this._mesh.parent === this._parent._mesh;
        const useWorldTransform = !!this._parent && !hasMirroredThreeParent;

        const pos = useWorldTransform
            ? this.transform.getWorldPosition()
            : (this.transform.position.value ?? Vector3.zero());
        const rot = useWorldTransform
            ? this.transform.getWorldQuaternion()
            : (this.transform.rotation.value ?? Quaternion.identity());
        const scl = useWorldTransform
            ? this.transform.getWorldScale()
            : (this.transform.scale.value ?? Vector3.one());

        // Always sync — checking isDirty alone misses cases where position was
        // set before the node entered the update loop (labels, async init).
        // The cost of redundant sets is negligible vs the bugs from skipping.
        this._mesh.position.set(pos.x, pos.y, pos.z);
        this._mesh.quaternion.set(rot.x, rot.y, rot.z, rot.w);
        this._mesh.scale.set(scl.x, scl.y, scl.z);
        this._mesh.matrixAutoUpdate = true;
    }

    // Override in subclasses for custom update logic
    protected onUpdate(deltaTime: number): void {
        // 🎯 DISABLED: Update our own animator if it exists
        // The old SceneNodeAnimator conflicts with the new Mesh3DAnimator system
        // Mesh3D shapes now use their own shapeAnimator instead
        // this._animator?.update(deltaTime);
    }

    // Visibility management
    setVisible(visible: boolean, recursive: boolean = false): void {
        this.visible = visible;
        
        if (recursive) {
            for (const child of this._children) {
                child.setVisible(visible, true);
            }
        }
    }

    isVisibleInHierarchy(): boolean {
        if (!this.visible) return false;
        
        let current: SceneNode | null = this._parent;
        while (current) {
            if (!current.visible) return false;
            current = current._parent;
        }
        
        return true;
    }

    // Active state management
    setActive(active: boolean, recursive: boolean = false): void {
        this.active = active;
        
        if (recursive) {
            for (const child of this._children) {
                child.setActive(active, true);
            }
        }
    }

    isActiveInHierarchy(): boolean {
        if (!this.active) return false;
        
        let current: SceneNode | null = this._parent;
        while (current) {
            if (!current.active) return false;
            current = current._parent;
        }
        
        return true;
    }

    // Cleanup
    destroy(): void {
        // Remove from parent
        if (this._parent) {
            this._parent.removeChild(this);
        }

        // Destroy all children
        const children = [...this._children]; // Copy to avoid modification during iteration
        for (const child of children) {
            child.destroy();
        }

        this.onDestroy();
    }

    protected onDestroy(): void {
        // Override in subclasses for cleanup
    }

    /**
     * Returns the world-space bounding box of the node.
     * It computes it if it's marked as dirty.
     */
    public getBoundingBox(): THREE.Box3 {
        if (this._isBoundingBoxDirty) {
            this.updateBoundingBox();
        }
        return this._boundingBox;
    }

    /**
     * Returns the bounding sphere derived from the bounding box.
     * Uses the existing bounding box system for consistency.
     */
    public getBoundingSphere(): { center: Vector3, radius: number } {
        const box = this.getBoundingBox(); // Uses existing cached box system
        
        // 🔍 DEBUG: Check if bounding box is valid
        //console.log(`🔍 [${this.name}] Bounding box debug:`, {
            //min: `(${box.min.x.toFixed(2)}, ${box.min.y.toFixed(2)}, ${box.min.z.toFixed(2)})`,
            //max: `(${box.max.x.toFixed(2)}, ${box.max.y.toFixed(2)}, ${box.max.z.toFixed(2)})`,
            //isEmpty: box.isEmpty()
        //});
        
        // Check for invalid box
        if (box.isEmpty()) {
            console.warn(`⚠️ [${this.name}] Bounding box is empty! Using fallback sphere.`);
            return {
                center: new Vector3(0, 0, 0),
                radius: 0.5 // Fallback radius
            };
        }
        
        const sphere = box.getBoundingSphere(new THREE.Sphere());
        
        // 🔍 DEBUG: Check sphere values
        //console.log(`🔍 [${this.name}] Sphere debug:`, {
            //center: `(${sphere.center.x.toFixed(2)}, ${sphere.center.y.toFixed(2)}, ${sphere.center.z.toFixed(2)})`,
            //radius: sphere.radius.toFixed(2)
        //});
        
        // Validate radius
        if (sphere.radius < 0 || !isFinite(sphere.radius)) {
            console.warn(`⚠️ [${this.name}] Invalid sphere radius: ${sphere.radius}. Using fallback.`);
            return {
                center: new Vector3(0, 0, 0),
                radius: 0.5 // Fallback radius
            };
        }
        
        return {
            center: new Vector3(sphere.center.x, sphere.center.y, sphere.center.z),
            radius: sphere.radius
        };
    }

    /**
     * Marks the bounding box as dirty, forcing a recalculation on next get.
     * This should be called whenever the node's geometry or transform changes.
     */
    public markBoundingBoxDirty(): void {
        this._isBoundingBoxDirty = true;
        // If this node's box is dirty, its parent's box is too.
        if (this._parent) {
            this._parent.markBoundingBoxDirty();
        }
    }

    /**
     * Abstract method for subclasses to implement their bounding box calculation.
     * This should compute the bounding box in the node's LOCAL space.
     */
    protected abstract calculateBoundingBox(): THREE.Box3;

    /**
     * Updates the world-space bounding box by calculating the local box
     * and applying the world transform.
     */
    protected updateBoundingBox(): void {
        const localBox = this.calculateBoundingBox();
        // Must apply the WORLD matrix to the LOCAL box to get a WORLD box.
        localBox.applyMatrix4(this.transform.getWorldMatrix());
        this._boundingBox.copy(localBox);
        this._isBoundingBoxDirty = false;
    }

    toString(): string {
        return `${this.constructor.name}(${this.name})`;
    }

    // UUID generation
    private generateUUID(): UUID {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    // Factory method
    static create(name: string = 'SceneNode'): SceneNode {
        throw new Error('SceneNode is abstract. Use a concrete subclass.');
    }

    public get animator(): SceneNodeAnimator {
        if (!this._animator) {
            this._animator = new SceneNodeAnimator(this);
        }
        return this._animator;
    }
}
